import {
  getObject,
  getOptionalObject,
  getList,
  getBoolean,
  getOptionalBoolean,
} from "@surya-digital/leo-ts-types";
import { SurveyLocalizedText, SurveyLocalizedTextDTO } from "../assets/surveyLocalizedText";
import { GridOptionV1, GridOptionV1DTO } from "./gridOptionV1";
import { GridColumnV1, GridColumnV1DTO } from "./gridColumnV1";
import { GridSize, GridSizeDTO } from "./gridSize";

export interface GridQuestionV1DTO {
  rowHeader?: SurveyLocalizedTextDTO | null;
  rowOptions: GridOptionV1DTO[];
  columnQuestions: GridColumnV1DTO[];
  areAllRowOptionsMandatoryIfRowAttempted: boolean;
  isChoiceResetAllowed?: boolean | null;
  gridRowSize?: GridSizeDTO | null;
  isUnanswered?: boolean | null;
}

export class GridQuestionV1 {
  readonly isGridQuestionV1GridQuestionV1: boolean = true;

  readonly rowHeader: SurveyLocalizedText | null;
  readonly rowOptions: GridOptionV1[];
  readonly columnQuestions: GridColumnV1[];
  readonly areAllRowOptionsMandatoryIfRowAttempted: boolean;
  readonly isChoiceResetAllowed: boolean | null;
  readonly gridRowSize: GridSize | null;
  readonly isUnanswered: boolean | null;

  static readonly constraints = {
    "rowOptions": {
      "minSize": 1,
      "maxSize": 32,
    },
    "columnQuestions": {
      "minSize": 1,
      "maxSize": 32,
    },
  }

  constructor(
  rowHeader: SurveyLocalizedText | null = null, 
  rowOptions: GridOptionV1[], 
  columnQuestions: GridColumnV1[], 
  areAllRowOptionsMandatoryIfRowAttempted: boolean, 
  isChoiceResetAllowed: boolean | null = null, 
  gridRowSize: GridSize | null = null, 
  isUnanswered: boolean | null = null
  ) {
    this.rowHeader = rowHeader;
    GridQuestionV1.validateRowOptions(rowOptions);
    this.rowOptions = rowOptions;
    GridQuestionV1.validateColumnQuestions(columnQuestions);
    this.columnQuestions = columnQuestions;
    this.areAllRowOptionsMandatoryIfRowAttempted = areAllRowOptionsMandatoryIfRowAttempted;
    this.isChoiceResetAllowed = isChoiceResetAllowed;
    this.gridRowSize = gridRowSize;
    this.isUnanswered = isUnanswered;
  }

  static fromDTO(dto: GridQuestionV1DTO): GridQuestionV1 {
    let rowHeader = getOptionalObject(dto, "rowHeader");
    if(rowHeader !== undefined && rowHeader !== null) {
      rowHeader = SurveyLocalizedText.fromDTO(getObject(dto, "rowHeader", InvalidGridQuestionV1RowHeaderError));
    }
    const rowOptions = getList<GridOptionV1DTO, GridOptionV1>(
      dto,
      "rowOptions",
      GridOptionV1.fromDTO,
      InvalidGridQuestionV1RowOptionsError
    );
    const columnQuestions = getList<GridColumnV1DTO, GridColumnV1>(
      dto,
      "columnQuestions",
      GridColumnV1.fromDTO,
      InvalidGridQuestionV1ColumnQuestionsError
    );
    const areAllRowOptionsMandatoryIfRowAttempted = getBoolean(dto, "areAllRowOptionsMandatoryIfRowAttempted", InvalidGridQuestionV1AreAllRowOptionsMandatoryIfRowAttemptedError);
    const isChoiceResetAllowed = getOptionalBoolean(dto, "isChoiceResetAllowed", InvalidGridQuestionV1IsChoiceResetAllowedError);
    let gridRowSize = getOptionalObject(dto, "gridRowSize");
    if(gridRowSize !== undefined && gridRowSize !== null) {
      gridRowSize = GridSize.fromDTO(getObject(dto, "gridRowSize", InvalidGridQuestionV1GridRowSizeError));
    }
    const isUnanswered = getOptionalBoolean(dto, "isUnanswered", InvalidGridQuestionV1IsUnansweredError);
    return new GridQuestionV1(
      rowHeader,
      rowOptions,
      columnQuestions,
      areAllRowOptionsMandatoryIfRowAttempted,
      isChoiceResetAllowed,
      gridRowSize,
      isUnanswered
    );
  }

  toDTO(): GridQuestionV1DTO {
    const dto: GridQuestionV1DTO = {
        rowOptions: this.rowOptions.map((e: GridOptionV1) => { return e.toDTO() }),
        columnQuestions: this.columnQuestions.map((e: GridColumnV1) => { return e.toDTO() }),
        areAllRowOptionsMandatoryIfRowAttempted: this.areAllRowOptionsMandatoryIfRowAttempted,
    };
    if (this.rowHeader !== undefined && this.rowHeader !== null){
      dto.rowHeader = this.rowHeader.toDTO();
    }
    if (this.isChoiceResetAllowed !== undefined && this.isChoiceResetAllowed !== null){
      dto.isChoiceResetAllowed = this.isChoiceResetAllowed;
    }
    if (this.gridRowSize !== undefined && this.gridRowSize !== null){
      dto.gridRowSize = this.gridRowSize.toDTO();
    }
    if (this.isUnanswered !== undefined && this.isUnanswered !== null){
      dto.isUnanswered = this.isUnanswered;
    }
    return dto;
  }
  private static validateRowOptions = (rowOptions: GridOptionV1[]): void => {
    if(rowOptions.length < 1) {
      throw new InvalidGridQuestionV1RowOptionsMinSizeError(
        1,
        `Attribute rowOptions is too short. Size is ${rowOptions.length}. Min size is 1.`
      );
    }
    if(rowOptions.length > 32) {
      throw new InvalidGridQuestionV1RowOptionsMaxSizeError(
        32,
        `Attribute rowOptions is too long. Size is ${rowOptions.length}. Max size is 32.`
      );
    }
  }
  private static validateColumnQuestions = (columnQuestions: GridColumnV1[]): void => {
    if(columnQuestions.length < 1) {
      throw new InvalidGridQuestionV1ColumnQuestionsMinSizeError(
        1,
        `Attribute columnQuestions is too short. Size is ${columnQuestions.length}. Min size is 1.`
      );
    }
    if(columnQuestions.length > 32) {
      throw new InvalidGridQuestionV1ColumnQuestionsMaxSizeError(
        32,
        `Attribute columnQuestions is too long. Size is ${columnQuestions.length}. Max size is 32.`
      );
    }
  }
  
}

GridQuestionV1.prototype.toString = function toString(): string {
  return `GridQuestionV1(rowHeader='${this.rowHeader}',rowOptions='${this.rowOptions}',columnQuestions='${this.columnQuestions}',areAllRowOptionsMandatoryIfRowAttempted='${this.areAllRowOptionsMandatoryIfRowAttempted}',isChoiceResetAllowed='${this.isChoiceResetAllowed}',gridRowSize='${this.gridRowSize}',isUnanswered='${this.isUnanswered}')`;
};

export class InvalidGridQuestionV1Error extends Error {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGridQuestionV1Error";
  }
}
export class InvalidGridQuestionV1RowHeaderError extends InvalidGridQuestionV1Error {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGridQuestionV1RowHeaderError";
  }
}

export class InvalidGridQuestionV1RowOptionsError extends InvalidGridQuestionV1Error {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGridQuestionV1RowOptionsError";
  }
}

export class InvalidGridQuestionV1RowOptionsMaxSizeError extends InvalidGridQuestionV1RowOptionsError {
  readonly maxSize: number;
  constructor(maxSize: number, message?: string) {
    super(message);
    this.maxSize = maxSize;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGridQuestionV1RowOptionsMaxSizeError";
  }
}

export class InvalidGridQuestionV1RowOptionsMinSizeError extends InvalidGridQuestionV1RowOptionsError {
  readonly minSize: number;
  constructor(minSize: number, message?: string) {
    super(message);
    this.minSize = minSize;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGridQuestionV1RowOptionsMinSizeError";
  }
}

export class InvalidGridQuestionV1ColumnQuestionsError extends InvalidGridQuestionV1Error {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGridQuestionV1ColumnQuestionsError";
  }
}

export class InvalidGridQuestionV1ColumnQuestionsMaxSizeError extends InvalidGridQuestionV1ColumnQuestionsError {
  readonly maxSize: number;
  constructor(maxSize: number, message?: string) {
    super(message);
    this.maxSize = maxSize;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGridQuestionV1ColumnQuestionsMaxSizeError";
  }
}

export class InvalidGridQuestionV1ColumnQuestionsMinSizeError extends InvalidGridQuestionV1ColumnQuestionsError {
  readonly minSize: number;
  constructor(minSize: number, message?: string) {
    super(message);
    this.minSize = minSize;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGridQuestionV1ColumnQuestionsMinSizeError";
  }
}

export class InvalidGridQuestionV1AreAllRowOptionsMandatoryIfRowAttemptedError extends InvalidGridQuestionV1Error {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGridQuestionV1AreAllRowOptionsMandatoryIfRowAttemptedError";
  }
}

export class InvalidGridQuestionV1IsChoiceResetAllowedError extends InvalidGridQuestionV1Error {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGridQuestionV1IsChoiceResetAllowedError";
  }
}

export class InvalidGridQuestionV1GridRowSizeError extends InvalidGridQuestionV1Error {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGridQuestionV1GridRowSizeError";
  }
}

export class InvalidGridQuestionV1IsUnansweredError extends InvalidGridQuestionV1Error {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGridQuestionV1IsUnansweredError";
  }
}
